import {
  CheckCircle as CheckCircleIcon,
  CreditCard as CreditCardIcon,
} from "@mui/icons-material";
import {
  Alert,
  alpha,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  Paper,
  Radio,
  RadioGroup,
  SvgIcon,
  SvgIconProps,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";

import { amazonColors } from "../../../config/amazonTheme";
import { MANUAL_PAYMENT_CONFIG } from "../../../config/paymentConfig";
import { createOrder } from "../../../services/orderService";
import {
  getAllPaymentMethodDiscounts,
  PaymentMethodDiscountValidationResult,
} from "../../../services/paymentMethodDiscountService";
import { paymentService } from "../../../services/paymentService";
import { useCartStore } from "../../../store/useCartStore";
import { DiscountType } from "../../../types/admin";
import {
  PaymentError,
  PaymentIntentResponse,
  PaymentSuccess,
  PriceUpdateResponse,
} from "../../../types/payment";
import Price from "../../common/Price";
import StripePaymentForm from "../payment/StripePaymentForm";
import ManualPaymentForm from "./ManualPaymentForm";

import type { CheckoutItem } from "./types";

// --- Custom Coin Icon Component ---
const CoinIcon = (props: SvgIconProps) => (
  <SvgIcon {...props} viewBox="0 0 24 24">
    <path
      d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM12 20C7.59 20 4 16.41 4 12C4 7.59 7.59 4 12 4C16.41 4 20 7.59 20 12C20 16.41 16.41 20 12 20ZM13 17H11V15H10V13H11V11H10V9H11V7H13V9H14V11H13V13H14V15H13V17Z"
      fill="currentColor"
    />
  </SvgIcon>
);

export interface PaymentCtaState {
  label: React.ReactNode;
  disabled: boolean;
  key: string;
  onClick?: () => void | Promise<void>;
}

export const PAYMENT_CTA_DEFAULT_STATE: PaymentCtaState = {
  label: "Complete payment below",
  disabled: true,
  key: "default",
};

interface PaymentSectionProps {
  finalTotal: number;
  customerEmail: string;
  customerName: string;
  items: CheckoutItem[];
  deliveryAddressId: string; // Address ID instead of full delivery info
  deliveryInstructions?: string; // Optional delivery instructions
  pickupLocation?: string; // Dushanbe or Khujand
  totalPrice: number;
  shippingCost: number;
  tax: number; // Tax amount (separate)
  fees: number; // Service fees (separate)
  onPaymentSuccess: (result: PaymentSuccess) => void;
  onPaymentError: (error: PaymentError) => void;
  onOrderNumberReceived?: (orderNumber: string) => void; // Callback to receive order number from backend
  onCtaStateChange?: (state: PaymentCtaState) => void;
  onPriceUpdate?: (updateData: {
    updatedTotalPrice?: number;
    updatedShippingCost?: number;
    updatedTax?: number;
    updatedFees?: number;
    updatedFinalTotal?: number;
  }) => void; // Callback to notify parent of price updates
  promotionCode?: string | null; // Applied promotion code
  promotionCodeId?: string | null; // Promotion code entity ID
  promotionDiscountAmount?: number; // Discount amount applied
  mobileSidebar?: React.ReactNode;
  onPaymentMethodDiscountChange?: (
    discount: PaymentMethodDiscountValidationResult | null
  ) => void; // Callback for payment method discount
}

type PaymentMethod = "stripe" | "alif" | "dcCard";

interface PaymentMethodOptionProps {
  value: PaymentMethod;
  selectedValue: PaymentMethod;
  onSelect: (value: PaymentMethod) => void;
  title: string;
  description: string | React.ReactNode;
  icon?: React.ReactNode;
  imageSrc?: string;
  savingBadge?: string;
}

const PaymentMethodOption: React.FC<PaymentMethodOptionProps> = React.memo(
  ({
    value,
    selectedValue,
    onSelect,
    title,
    description,
    icon,
    imageSrc,
    savingBadge,
  }) => {
    const isSelected = value === selectedValue;

    return (
      <Paper
        elevation={isSelected ? 2 : 0}
        onClick={() => onSelect(value)}
        sx={{
          position: "relative",
          mb: 2,
          p: 2,
          pt: savingBadge ? { xs: 4, sm: 2 } : 2,
          border: "2px solid",
          borderColor: isSelected ? amazonColors.orange : "transparent",
          backgroundColor: isSelected
            ? alpha(amazonColors.orange, 0.04)
            : "background.paper",
          borderRadius: 3,
          cursor: "pointer",
          transition: "all 0.2s ease-in-out",
          overflow: "hidden",
          "&:hover": {
            backgroundColor: isSelected
              ? alpha(amazonColors.orange, 0.08)
              : "grey.50",
            borderColor: isSelected ? amazonColors.orange : "grey.300",
          },
        }}
      >
        {savingBadge && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              right: 0,
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              backgroundColor: "#10b981",
              color: "white",
              borderRadius: "0 0 0 12px",
              padding: "6px 12px 6px 10px",
              fontSize: "0.75rem",
              fontWeight: 700,
              boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
              zIndex: 2,
              pointerEvents: "none",
            }}
          >
            <CoinIcon sx={{ fontSize: 15 }} />
            {savingBadge}
          </Box>
        )}

        <FormControlLabel
          value={value}
          control={<Radio checked={isSelected} />}
          sx={{
            width: "100%",
            m: 0,
            alignItems: "flex-start",
            "& .MuiRadio-root": {
              mt: 0.5,
            },
            "& .MuiFormControlLabel-label": {
              width: "100%",
            },
          }}
          label={
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: 2,
                ml: 1,
                width: "100%",
              }}
            >
              <Box
                sx={{
                  width: 50,
                  height: 50,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  bgcolor: "white",
                  borderRadius: 2,
                  p: 0.5,
                  border: "1px solid",
                  borderColor: "grey.200",
                }}
              >
                {imageSrc ? (
                  <Box
                    component="img"
                    src={imageSrc}
                    alt={title}
                    sx={{
                      width: "100%",
                      height: "100%",
                      objectFit: "contain",
                    }}
                  />
                ) : (
                  icon
                )}
              </Box>

              <Box
                sx={{
                  flex: 1,
                  paddingRight: savingBadge ? { xs: 0, sm: "80px" } : 0,
                }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 700, lineHeight: 1.3, mb: 0.5 }}
                  className="notranslate"
                  translate="no"
                >
                  {title}
                </Typography>
                <Box
                  sx={{
                    lineHeight: 1.4,
                    fontSize: "0.85rem",
                    color: "text.secondary",
                  }}
                  className="notranslate"
                  translate="no"
                >
                  {description}
                </Box>
              </Box>
            </Box>
          }
        />
      </Paper>
    );
  }
);

PaymentMethodOption.displayName = "PaymentMethodOption";

const PaymentSection: React.FC<PaymentSectionProps> = ({
  finalTotal,
  customerEmail,
  customerName,
  items,
  deliveryAddressId,
  deliveryInstructions,
  pickupLocation,
  totalPrice,
  shippingCost,
  tax,
  fees,
  onPaymentSuccess,
  onPaymentError,
  onOrderNumberReceived,
  onCtaStateChange,
  onPriceUpdate,
  promotionCode,
  promotionCodeId,
  promotionDiscountAmount,
  mobileSidebar,
  onPaymentMethodDiscountChange,
}) => {
  const [selectedPaymentMethod, setSelectedPaymentMethod] =
    useState<PaymentMethod>("alif");
  const { t: myTranslate } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [showPaymentFormInternal, setShowPaymentFormInternal] = useState(false);
  const [paymentIntent, setPaymentIntent] =
    useState<PaymentIntentResponse | null>(null);
  const [isCreatingIntent, setIsCreatingIntent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [priceUpdateData, setPriceUpdateData] =
    useState<PriceUpdateResponse | null>(null);
  const [paymentMethodDiscount, setPaymentMethodDiscount] =
    useState<PaymentMethodDiscountValidationResult | null>(null);
  const [alifDiscount, setAlifDiscount] =
    useState<PaymentMethodDiscountValidationResult | null>(null);
  const [dcCardDiscount, setDcCardDiscount] =
    useState<PaymentMethodDiscountValidationResult | null>(null);
  const [isLoadingDiscount, setIsLoadingDiscount] = useState(false);
  const { updatePricesFromValidation } = useCartStore();
  const paymentFormSubmitRef = useRef<(() => Promise<void>) | null>(null);
  const itemsRef = useRef<CheckoutItem[]>(items);
  const lastCtaStateRef = useRef<PaymentCtaState | null>(null);
  const createIntentRequestIdRef = useRef(0);
  const [hasPaymentFormSubmit, setHasPaymentFormSubmit] = useState(false);
  const [isPaymentSubmitting, setIsPaymentSubmitting] = useState(false);
  const [showManualPaymentForm, setShowManualPaymentForm] = useState(false);

  useEffect(() => {
    itemsRef.current = items;
  }, [items]);

  useEffect(() => {
    if (!isMobile) return;
    if (!showPaymentFormInternal && !showManualPaymentForm) return;
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [isMobile, showManualPaymentForm, showPaymentFormInternal]);

  const clearPaymentForms = useCallback(() => {
    setShowManualPaymentForm(false);
    setShowPaymentFormInternal(false);
    setPaymentIntent(null);
    paymentFormSubmitRef.current = null;
    setHasPaymentFormSubmit(false);
    setIsPaymentSubmitting(false);
  }, []);

  const cancelCreateIntent = useCallback(() => {
    createIntentRequestIdRef.current += 1;
    setIsCreatingIntent(false);
  }, []);

  const updatePaymentMethodAndDiscount = useCallback(
    (newMethod: PaymentMethod) => {
      setSelectedPaymentMethod(newMethod);
    },
    []
  );

  const handlePaymentMethodSelect = useCallback(
    (method: PaymentMethod) => {
      cancelCreateIntent();
      clearPaymentForms();
      setError(null);
      setPriceUpdateData(null);
      updatePaymentMethodAndDiscount(method);
    },
    [cancelCreateIntent, clearPaymentForms, updatePaymentMethodAndDiscount]
  );

  const handlePaymentMethodChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newMethod = event.target.value as PaymentMethod;
      handlePaymentMethodSelect(newMethod);
    },
    [handlePaymentMethodSelect]
  );

  const registerPaymentFormSubmit = useCallback(
    (handler: (() => Promise<void>) | null) => {
      paymentFormSubmitRef.current = handler;
      setHasPaymentFormSubmit(Boolean(handler));
    },
    []
  );

  const handlePaymentProcessingChange = useCallback((isProcessing: boolean) => {
    setIsPaymentSubmitting(isProcessing);
  }, []);

  const handlePayNow = useCallback(async () => {
    if (paymentFormSubmitRef.current) {
      await paymentFormSubmitRef.current();
    }
  }, []);

  // Fetch all payment method discounts when component mounts or totals change
  const fetchAllPaymentMethodDiscounts = useCallback(async () => {
    if (totalPrice <= 0) return;

    try {
      setIsLoadingDiscount(true);
      const response = await getAllPaymentMethodDiscounts({
        orderValue: totalPrice,
        fees,
        tax: tax,
      });

      if (response.isSuccess && response.data) {
        setAlifDiscount(response.data.alifDiscount || null);
        setDcCardDiscount(response.data.dcCardDiscount || null);
      } else {
        setAlifDiscount(null);
        setDcCardDiscount(null);
      }
    } catch (error) {
      console.error("Error fetching all payment method discounts:", error);
      setAlifDiscount(null);
      setDcCardDiscount(null);
    } finally {
      setIsLoadingDiscount(false);
    }
  }, [totalPrice, fees, tax]);

  // Fetch all discounts on mount and when totals change
  useEffect(() => {
    fetchAllPaymentMethodDiscounts();
  }, [fetchAllPaymentMethodDiscounts]);

  useEffect(() => {
    let nextDiscount: PaymentMethodDiscountValidationResult | null = null;

    if (selectedPaymentMethod === "alif") {
      nextDiscount = alifDiscount;
    } else if (selectedPaymentMethod === "dcCard") {
      nextDiscount = dcCardDiscount;
    }

    setPaymentMethodDiscount(nextDiscount);
    if (onPaymentMethodDiscountChange) {
      onPaymentMethodDiscountChange(nextDiscount);
    }
  }, [
    selectedPaymentMethod,
    alifDiscount,
    dcCardDiscount,
    onPaymentMethodDiscountChange,
  ]);

  // Helper function to format discount display
  const formatDiscountBadge = (
    discount: PaymentMethodDiscountValidationResult | null
  ): string | null => {
    if (!discount || !discount.isValid || !discount.discount) {
      return null;
    }

    const discountAmount = discount.discountAmount ?? 0;
    const { discountType, discountValue } = discount.discount;

    if (discountAmount <= 0 || discountValue <= 0) {
      return null;
    }

    if (discountType === DiscountType.Percentage) {
      return `${discountValue}% OFF`;
    }
    return `$${discountValue.toFixed(2)} OFF`;
  };

  const createPaymentIntent = useCallback(async () => {
    const requestId = ++createIntentRequestIdRef.current;
    try {
      setIsCreatingIntent(true);
      setError(null);
      setPriceUpdateData(null);

      const normalizedCustomerName = (customerName || "").trim();

      const response = await paymentService.createPaymentIntent({
        amount: finalTotal,
        currency: "usd",
        // orderNumber is now optional - backend will generate it
        customerEmail,
        customerName: normalizedCustomerName,
        items: itemsRef.current.map((item) => ({
          productId: item.productId,
          title: item.title,
          price: item.price,
          quantity: item.quantity,
          image: item.image,
          sku: item.productId,
          // Backend still expects cargoPrice; map per-item shipping to it
          cargoPrice: item.shippingCost ?? 0,
          // Add dropshipping fields
          retailer: item.retailer || "Unknown", // Get retailer from cart item
          estimatedWeight: item.estimatedWeight, // Estimated weight in kg
          sellerName: item.sellerName,
          sellerId: item.sellerId,
          offerId: item.offerId,
          sellerShippingFee: item.sellerShippingFee,
        })),
        deliveryAddressId: deliveryAddressId,
        deliveryInstructions: deliveryInstructions,
        pickupLocation: pickupLocation,
        totalPrice: totalPrice,
        shippingCost: shippingCost,
        tax: tax,
        fees: fees,
        finalTotal: finalTotal,
        // Promotion code information
        promotionCode: promotionCode || undefined,
        promotionCodeId: promotionCodeId || undefined,
        promotionDiscountAmount: promotionDiscountAmount || undefined,
      });

      if (createIntentRequestIdRef.current !== requestId) {
        return;
      }

      // Check if this is a price update response (not a real payment intent)
      // Note: Backend returns camelCase in JSON (requiresPriceUpdate, priceUpdateData)
      if (response.requiresPriceUpdate && response.priceUpdateData) {
        const priceUpdate = response.priceUpdateData;
        setPriceUpdateData(priceUpdate);

        // Update cart with new prices
        const updates = priceUpdate.updatedItems.map((item) => ({
          productId: item.productId,
          newPrice: item.newPrice,
          newShippingCost: item.newCargoPrice,
        }));

        updatePricesFromValidation(updates);

        // Notify parent component of price updates so it can recalculate totals
        if (onPriceUpdate) {
          onPriceUpdate({
            updatedTotalPrice: priceUpdate.updatedTotalPrice,
            updatedShippingCost: priceUpdate.updatedShippingCost,
            updatedTax: priceUpdate.updatedTax,
            updatedFees: priceUpdate.updatedFees,
            updatedFinalTotal: priceUpdate.updatedFinalTotal,
          });
        }

        setError(priceUpdate.message || myTranslate("checkout.pricesUpdated"));

        // Scroll to top to show the message
        window.scrollTo({ top: 0, behavior: "smooth" });
        return; // Don't set paymentIntent or show payment form
      }

      // Validate that we have required fields for payment
      if (!response.clientSecret || !response.paymentIntentId) {
        setError(myTranslate("checkout.paymentIntentMissing"));
        return;
      }

      // Notify parent component about the order number
      if (onOrderNumberReceived && response.orderNumber) {
        onOrderNumberReceived(response.orderNumber);
      }

      setPaymentIntent(response);
      setShowPaymentFormInternal(true);
    } catch (error: unknown) {
      if (createIntentRequestIdRef.current !== requestId) {
        return;
      }

      console.error("Error creating payment intent:", error);

      // Check if this is a price update error (422)
      if (
        typeof error === "object" &&
        error !== null &&
        "priceUpdateData" in error &&
        (error as { priceUpdateData?: PriceUpdateResponse }).priceUpdateData
      ) {
        const priceUpdate = (error as { priceUpdateData: PriceUpdateResponse })
          .priceUpdateData;
        setPriceUpdateData(priceUpdate);

        // Update cart with new prices
        const updates = priceUpdate.updatedItems.map((item) => ({
          productId: item.productId,
          newPrice: item.newPrice,
          newShippingCost: item.newCargoPrice,
        }));

        updatePricesFromValidation(updates);

        // Notify parent component of price updates so it can recalculate totals
        if (onPriceUpdate) {
          onPriceUpdate({
            updatedTotalPrice: priceUpdate.updatedTotalPrice,
            updatedShippingCost: priceUpdate.updatedShippingCost,
            updatedTax: priceUpdate.updatedTax,
            updatedFees: priceUpdate.updatedFees,
            updatedFinalTotal: priceUpdate.updatedFinalTotal,
          });
        }

        setError(priceUpdate.message || myTranslate("checkout.pricesUpdated"));

        // Scroll to top to show the message
        window.scrollTo({ top: 0, behavior: "smooth" });
      } else {
        const errorMessage =
          error instanceof Error
            ? error.message
            : myTranslate("checkout.pricesUpdated");
        setError(errorMessage);
      }
    } finally {
      if (createIntentRequestIdRef.current === requestId) {
        setIsCreatingIntent(false);
      }
    }
  }, [
    customerEmail,
    customerName,
    deliveryAddressId,
    deliveryInstructions,
    fees,
    finalTotal,
    pickupLocation,
    shippingCost,
    tax,
    totalPrice,
    onOrderNumberReceived,
    updatePricesFromValidation,
    onPriceUpdate,
    myTranslate,
    promotionCode,
    promotionCodeId,
    promotionDiscountAmount,
  ]);

  const handleProceedToPayment = useCallback(async () => {
    if (selectedPaymentMethod === "stripe") {
      await createPaymentIntent();
    } else if (
      selectedPaymentMethod === "alif" ||
      selectedPaymentMethod === "dcCard"
    ) {
      setShowManualPaymentForm(true);
    }
  }, [createPaymentIntent, selectedPaymentMethod]);

  const handleManualPaymentSubmit = useCallback(
    async (data: {
      customerPhone: string;
      screenshotUrl?: string;
    }) => {
      try {
        setIsPaymentSubmitting(true);

        // Create order with manual payment details
        const orderData = {
          userId: "", // Will be set by backend from JWT
          totalPrice: totalPrice,
          shippingCost: shippingCost,
          tax: tax,
          fees: fees || 0,
          finalTotal: finalTotal,
          items: items.map((item) => ({
            productId: item.productId,
            title: item.title,
            price: item.price,
            quantity: item.quantity,
            image: item.image,
            retailer: item.retailer || "Unknown",
            estimatedWeight: item.estimatedWeight,
            weightInKg: item.estimatedWeight,
            cargoPrice: item.shippingCost || 0,
            sellerName: item.sellerName,
            sellerId: item.sellerId,
            offerId: item.offerId,
            sellerShippingFee: item.sellerShippingFee,
          })),
          deliveryAddressId: deliveryAddressId,
          deliveryInstructions: deliveryInstructions,
          pickupLocation: pickupLocation || "",
          paymentMethodType: selectedPaymentMethod === "alif" ? 2 : 3, // Alif = 2, DcCard = 3 (from PaymentMethodType enum)
          manualPaymentDetails: {
            transactionId: "0",
            customerPhone: data.customerPhone,
            screenshotUrl: data.screenshotUrl,
          },
          // Promotion code information
          promotionCode: promotionCode || undefined,
          promotionCodeId: promotionCodeId || undefined,
          promotionDiscountAmount: promotionDiscountAmount || undefined,
          discountAmount: promotionDiscountAmount || undefined,
          // Payment method discount information
          paymentMethodDiscountId: paymentMethodDiscount?.discount?.id,
          paymentMethodDiscountAmount: paymentMethodDiscount?.discountAmount,
        };

        const response = await createOrder(orderData);

        if (response.isSuccess && response.data) {
          // Notify parent about order number
          if (onOrderNumberReceived && response.data.orderNumber) {
            onOrderNumberReceived(response.data.orderNumber);
          }

          // Call success callback
          onPaymentSuccess({
            orderNumber: response.data.orderNumber,
            paymentIntentId: "manual",
            amount: finalTotal,
            currency: "usd",
          });
        } else {
          throw new Error(response.message || "Failed to create order");
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to submit payment details";
        onPaymentError({
          message: errorMessage,
          code: "MANUAL_PAYMENT_ERROR",
        });
      } finally {
        setIsPaymentSubmitting(false);
      }
    },
    [
      totalPrice,
      shippingCost,
      tax,
      fees,
      finalTotal,
      items,
      deliveryAddressId,
      deliveryInstructions,
      pickupLocation,
      selectedPaymentMethod,
      promotionCode,
      promotionCodeId,
      promotionDiscountAmount,
      onOrderNumberReceived,
      onPaymentSuccess,
      onPaymentError,
      paymentMethodDiscount,
    ]
  );

  const ctaState = useMemo<PaymentCtaState>(() => {
    let label: React.ReactNode;
    let disabled: boolean;
    let action: (() => void | Promise<void>) | undefined;
    let key: string;

    const loadingLabel = (text: string) => (
      <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1 }}>
        <CircularProgress size={18} color="inherit" />
        <span>{text}</span>
      </Box>
    );

    const payLabel = (
      <>
        {myTranslate("checkout.pay")}{" "}
        <Price value={finalTotal} disableCentsHeuristic />
      </>
    );

    if (isPaymentSubmitting) {
      label = loadingLabel(myTranslate("checkout.processing"));
      disabled = true;
      key = "processing";
    } else if (isCreatingIntent) {
      label = loadingLabel(myTranslate("checkout.preparingPayment"));
      disabled = true;
      key = "processing";
    } else if (showPaymentFormInternal) {
      label = payLabel;
      disabled = isPaymentSubmitting || !hasPaymentFormSubmit;
      action = handlePayNow;
      key = "pay";
    } else if (selectedPaymentMethod === "stripe") {
      label = myTranslate("checkout.continueWithCard");
      disabled = false;
      action = handleProceedToPayment;
      key = "continue-card";
    } else if (selectedPaymentMethod === "alif") {
      label = showManualPaymentForm
        ? payLabel
        : myTranslate("checkout.continueWithAlif");
      disabled = showManualPaymentForm ? isPaymentSubmitting : false;
      action = showManualPaymentForm ? handlePayNow : handleProceedToPayment;
      key = showManualPaymentForm ? "pay-alif" : "continue-alif";
    } else if (selectedPaymentMethod === "dcCard") {
      label = showManualPaymentForm
        ? payLabel
        : myTranslate("checkout.continueWithDcCard");
      disabled = showManualPaymentForm ? isPaymentSubmitting : false;
      action = showManualPaymentForm ? handlePayNow : handleProceedToPayment;
      key = showManualPaymentForm ? "pay-dc" : "continue-dc";
    } else {
      label = myTranslate("checkout.completePaymentBelow");
      disabled = true;
      key = "default";
    }

    const nextState: PaymentCtaState = {
      label,
      disabled,
      key,
      onClick: disabled ? undefined : action,
    };
    return nextState;
  }, [
    finalTotal,
    handlePayNow,
    handleProceedToPayment,
    hasPaymentFormSubmit,
    isCreatingIntent,
    isPaymentSubmitting,
    selectedPaymentMethod,
    showPaymentFormInternal,
    showManualPaymentForm,
    myTranslate,
  ]);

  useEffect(() => {
    if (!onCtaStateChange) return;

    lastCtaStateRef.current = ctaState;
    onCtaStateChange(ctaState);
  }, [ctaState, onCtaStateChange]);

  useEffect(() => {
    if (!onCtaStateChange) return;
    return () => {
      onCtaStateChange(PAYMENT_CTA_DEFAULT_STATE);
    };
  }, [onCtaStateChange]);

  const alifBadge = isLoadingDiscount
    ? null
    : formatDiscountBadge(alifDiscount);
  const dcCardBadge = isLoadingDiscount
    ? null
    : formatDiscountBadge(dcCardDiscount);

  const PAYMENT_METHODS_CONFIG = useMemo(
    () => [
      {
        value: "alif" as PaymentMethod,
        title: myTranslate("checkout.payWithAlif"),
        description: MANUAL_PAYMENT_CONFIG.alif.description,
        imageSrc: "/logos/alif.svg",
        savingBadge: alifBadge || undefined,
      },
      {
        value: "dcCard" as PaymentMethod,
        title: myTranslate("checkout.payWithDcCard"),
        description: MANUAL_PAYMENT_CONFIG.dcCard.description,
        imageSrc: "/logos/dv.svg",
        savingBadge: dcCardBadge || undefined,
      },
      {
        value: "stripe" as PaymentMethod,
        title: myTranslate("checkout.creditDebitCard"),
        description: (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: { xs: "0px", sm: "2px" },
              mt: 0.5,
              flexWrap: { xs: "nowrap", sm: "wrap" },
              overflowX: { xs: "auto", sm: "visible" },
            }}
          >
            {[
              "visa.png",
              "master.png",
              "american-express.png",
              "discover.png",
              "JCB_Japan_Credit_Bureau.png",
              "China_UnionPay.png",
              "eftpos_Australia.png",
            ].map((fileName) => (
              <Box
                component="img"
                key={fileName}
                src={`/logos/payment_methods/${fileName}`}
                alt={fileName.replace(".png", "")}
                sx={{
                  height: { xs: 10, sm: 15 },
                  width: { xs: 18, sm: 28 },
                  objectFit: "contain",
                  flexShrink: 0,
                }}
              />
            ))}
          </Box>
        ),
        icon: (
          <CreditCardIcon sx={{ color: amazonColors.orange, fontSize: 32 }} />
        ),
        savingBadge: undefined,
      },
    ],
    [myTranslate, alifBadge, dcCardBadge]
  );

  const renderPaymentMethodSelection = () => (
    <Box>
      <Typography
        variant="body1"
        sx={{ mb: 3, color: "text.secondary", fontWeight: 500 }}
        className="notranslate"
        translate="no"
      >
        {myTranslate("checkout.choosePaymentMethod")}
      </Typography>

      <Collapse in={!!error}>
        {error && (
          <Alert
            severity={priceUpdateData ? "warning" : "error"}
            sx={{ mb: 3 }}
            onClose={() => {
              setError(null);
              setPriceUpdateData(null);
            }}
          >
            <Typography
              variant="body1"
              sx={{ fontWeight: 600, mb: priceUpdateData ? 1 : 0 }}
            >
              {error}
            </Typography>
            {priceUpdateData && (
              <Box sx={{ mt: 2 }}>
                <Typography
                  variant="body2"
                  sx={{ mb: 1, fontWeight: 600 }}
                  className="notranslate"
                  translate="no"
                >
                  {myTranslate("checkout.priceChanges")}
                </Typography>
                {priceUpdateData.updatedItems
                  .filter(
                    (item) =>
                      Math.abs(item.oldPrice - item.newPrice) > 0.01 ||
                      Math.abs(item.oldCargoPrice - item.newCargoPrice) > 0.01
                  )
                  .map((item) => (
                    <Box key={item.productId} sx={{ mb: 1, pl: 2 }}>
                      <Typography variant="body2">
                        <strong>{item.title}</strong>
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {Math.abs(item.oldPrice - item.newPrice) > 0.01 && (
                          <>
                            {myTranslate("checkout.priceLabel")}{" "}
                            <Price
                              value={item.oldPrice}
                              disableCentsHeuristic
                            />{" "}
                            →{" "}
                            <Price
                              value={item.newPrice}
                              disableCentsHeuristic
                            />
                          </>
                        )}
                        {Math.abs(item.oldPrice - item.newPrice) > 0.01 &&
                          Math.abs(item.oldCargoPrice - item.newCargoPrice) >
                            0.01 && <> | </>}
                        {Math.abs(item.oldCargoPrice - item.newCargoPrice) >
                          0.01 && (
                          <>
                            {myTranslate("checkout.shippingLabel")}{" "}
                            <Price
                              value={item.oldCargoPrice}
                              disableCentsHeuristic
                            />{" "}
                            →{" "}
                            <Price
                              value={item.newCargoPrice}
                              disableCentsHeuristic
                            />
                          </>
                        )}
                      </Typography>
                    </Box>
                  ))}
                <Typography
                  variant="body2"
                  sx={{ mt: 2, fontWeight: 600 }}
                  className="notranslate"
                  translate="no"
                >
                  {myTranslate("checkout.newTotal")}{" "}
                  <Price
                    value={priceUpdateData.updatedFinalTotal}
                    disableCentsHeuristic
                  />
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mt: 1 }}
                  className="notranslate"
                  translate="no"
                >
                  {myTranslate("checkout.cartUpdatedWithNewPrices")}
                </Typography>
              </Box>
            )}
          </Alert>
        )}
      </Collapse>

      <FormControl component="fieldset" sx={{ width: "100%" }}>
        <FormLabel
          component="legend"
          sx={{ mb: 2, fontWeight: 600, color: "text.primary" }}
          className="notranslate"
          translate="no"
        >
          {myTranslate("checkout.paymentMethods")}
        </FormLabel>

        <RadioGroup
          value={selectedPaymentMethod}
          onChange={handlePaymentMethodChange}
        >
          <Grid container spacing={0}>
            {PAYMENT_METHODS_CONFIG.map((method) => (
              <Grid item xs={12} key={method.value}>
                <PaymentMethodOption
                  value={method.value}
                  selectedValue={selectedPaymentMethod}
                  onSelect={handlePaymentMethodSelect}
                  title={method.title}
                  description={method.description}
                  icon={method.icon}
                  imageSrc={method.imageSrc}
                  savingBadge={method.savingBadge}
                />
              </Grid>
            ))}
          </Grid>
        </RadioGroup>
      </FormControl>

      {selectedPaymentMethod === "stripe" && (
        <Alert
          severity="warning"
          icon={<CreditCardIcon fontSize="inherit" />}
          sx={{ mt: 1, mb: 1, borderRadius: 2 }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
            {myTranslate("checkout.stripeFeesNotice")}
          </Typography>
          <Typography variant="body2">
            {myTranslate("checkout.stripeFeesNoticeDetail")}
          </Typography>
        </Alert>
      )}

      {isMobile && mobileSidebar && <Box sx={{ mt: 2 }}>{mobileSidebar}</Box>}
    </Box>
  );

  const renderPaymentForm = () => {
    switch (selectedPaymentMethod) {
      case "stripe":
        return (
          <StripePaymentForm
            amount={finalTotal}
            orderNumber={paymentIntent?.orderNumber || ""}
            customerEmail={customerEmail}
            customerName={customerName}
            paymentIntent={paymentIntent}
            onPaymentSuccess={onPaymentSuccess}
            onPaymentError={onPaymentError}
            onRegisterSubmit={registerPaymentFormSubmit}
            onProcessingChange={handlePaymentProcessingChange}
            mobileSidebar={mobileSidebar}
          />
        );
      case "alif":
      case "dcCard":
        return (
          <ManualPaymentForm
            paymentMethod={selectedPaymentMethod}
            amount={finalTotal}
            onSubmit={handleManualPaymentSubmit}
            mobileSidebar={mobileSidebar}
            onCancel={() => {
              cancelCreateIntent();
              clearPaymentForms();
            }}
            onFormReady={registerPaymentFormSubmit}
            onSubmitStateChange={handlePaymentProcessingChange}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Card
      elevation={2}
      className="notranslate"
      translate="no"
      sx={{ borderRadius: 2 }}
    >
      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
        <Box sx={{ minHeight: 300 }}>
          <Collapse
            in={!showPaymentFormInternal && !showManualPaymentForm}
            unmountOnExit
          >
            {renderPaymentMethodSelection()}
          </Collapse>

          <Collapse
            in={showPaymentFormInternal || showManualPaymentForm}
            unmountOnExit
          >
            {(showPaymentFormInternal || showManualPaymentForm) && (
              <Box sx={{ mb: 2 }}>
                <Chip
                  icon={<CheckCircleIcon />}
                  label={
                    PAYMENT_METHODS_CONFIG.find(
                      (method) => method.value === selectedPaymentMethod
                    )?.title
                  }
                  color="primary"
                  variant="outlined"
                  onDelete={() => {
                    cancelCreateIntent();
                    clearPaymentForms();
                  }}
                />
              </Box>
            )}
            {renderPaymentForm()}
          </Collapse>
        </Box>
      </CardContent>
    </Card>
  );
};

export default PaymentSection;
